import { useCallback, useEffect, useRef } from "react";
import { CommandsDirectory, CommandsOptions } from "./lib";
import { useCommands } from "./useCommands";

interface UseOwnedCommandPaletteParams<TKind extends string> {
    optionsByKind: Record<TKind, CommandsOptions>;
    selectedCount: number;
}

/**
 * Opens the palette with options that stay live while it is open, and closes
 * it once the selection it acts on empties. Only touches a palette this hook
 * opened, so other openers keep their own options.
 */
export function useOwnedCommandPalette<TKind extends string>({
    optionsByKind,
    selectedCount,
}: UseOwnedCommandPaletteParams<TKind>) {
    const { open, close, replaceOptions, isOpen } = useCommands();
    const ownedKindRef = useRef<TKind | null>(null);
    const previousSelectedCountRef = useRef(selectedCount);

    const openPalette = useCallback(
        (directory: CommandsDirectory, kind: TKind) => {
            ownedKindRef.current = kind;
            open(directory, optionsByKind[kind]);
        },
        [open, optionsByKind],
    );

    useEffect(() => {
        const previousSelectedCount = previousSelectedCountRef.current;
        previousSelectedCountRef.current = selectedCount;

        if (!isOpen) {
            ownedKindRef.current = null;
            return;
        }
        const ownedKind = ownedKindRef.current;
        if (ownedKind === null) {
            return;
        }
        if (selectedCount === 0 && previousSelectedCount > 0) {
            close();
            return;
        }
        replaceOptions(optionsByKind[ownedKind]);
    }, [isOpen, selectedCount, optionsByKind, close, replaceOptions]);

    return { openPalette };
}
